//#region Util Imports

import * as _ from 'underscore'

//#endregion

import { IOpData, IReferenceType, IOpChangeState, IJsonSerializable, IOpObject, ICopyable } from '../util/interfaces'
import { OpDataList } from '../util/op-data-list'
import { Utils } from '../util/utils'

import { Enums } from '../common/enums'

import { DataProvider } from '../../helpers/data-provider'

import { OpAlarmGroupData } from './op-alarm-group-data'
import { OpDistributor } from './op-distributor'
import { OpAlarmDefGroup } from './op-alarm-def-group'
import { OpAlarmGroupOperator } from './op-alarm-group-operator'

export class OpAlarmGroup
    implements IReferenceType
    , IOpObject
    , IOpChangeState
    , IJsonSerializable
    , ICopyable<OpAlarmGroup>
{
    // #region Properties

    public idAlarmGroup: number
    public name: string
    public description: string
    public idDistributor: number
    public isEnabled: boolean

    // #endregion

    // #region Navigation Properties

    public distributor: OpDistributor

    // #endregion

    //#region Custom Properties
    public currentAlarmDefGroups: OpAlarmDefGroup[]
    public currentAlarmGroupOperators: OpAlarmGroupOperator[]
    //#endregion

    // #region Ctor

    constructor(opObj: any) {
        if (opObj instanceof OpAlarmGroup) {
            this.copy(<OpAlarmGroup>opObj, true, true, true, true)
        } else {
            this.fromJSON(opObj)
        }
    }
    // #endregion

    // #region IReferenceType

    public getReferenceType(): Enums.ReferenceType {
        return Enums.ReferenceType.IdAlarmGroup
    }

    public getReferenceKey(): number {
        return this.idAlarmGroup
    }

    public getReferenceValue(): any {
        return this
    }

    public toString(): string {
        return this.name
    }

    // #endregion

    // #region IOpObject

    public get idObject(): number {
        return this.idAlarmGroup
    }

    public set idObject(idObject: number) {
        this.idAlarmGroup = idObject
    }

    public dataList: OpDataList<OpAlarmGroupData> = new OpDataList<OpAlarmGroupData>()

    public customFormatString: string

    public static fromIOpObject(object: IOpObject): OpAlarmGroup {
        const newObject: OpAlarmGroup = new OpAlarmGroup(null)

        newObject.idAlarmGroup = object.idObject
        newObject.opState = object.opState

        if (object.dataList != null) {
            newObject.dataList.push(...object.dataList.toArray().map((data: IOpData) => OpAlarmGroupData.fromIOpData(data)))
        }

        newObject.idDistributor = object.dataList.getValue(Enums.DataType.HELPER_ID_DISTRIBUTOR)
        newObject.description = object.dataList.getValue(Enums.DataType.HELPER_DESCRIPTION)
        newObject.name = object.dataList.getValue(Enums.DataType.HELPER_NAME)
        newObject.isEnabled = object.dataList.getValue(Enums.DataType.HELPER_IS_ENABLED)

        newObject.currentAlarmDefGroups = []
        newObject.currentAlarmGroupOperators = []

        return newObject
    }
    // #endregion

    // #region IOpDynamic

    public dynamicValues: { Key: string, Value: any }[] = []

    // #endregion

    // #region IOpChangeState

    public opState: Enums.OpChangeState

    // #endregion

    // #region IJsonSerializable

    public fromJSON(opObj: any, compressed: boolean = false): void {
        if (opObj != null) {

            if (opObj.DataList != null) {
                this.dataList.push(...opObj.DataList.map((data: OpAlarmGroupData) => new OpAlarmGroupData(data)))
            }

            this.idAlarmGroup = opObj.ID_ALARM_GROUP
            this.name = opObj.NAME
            this.description = opObj.DESCRIPTION
            this.idDistributor = opObj.ID_DISTRIBUTOR
            this.isEnabled = opObj.IS_ENABLED

            this.distributor = opObj._Distributor == null ? null : new OpDistributor(opObj._Distributor)

            this.dynamicValues = opObj.dynamicValues

            this.opState = opObj.OpState

            this.currentAlarmGroupOperators = (_.size(opObj.CurrentAlarmGroupOperators) > 0) ? _.map(opObj.CurrentAlarmGroupOperators, (operator: OpAlarmGroupOperator) => new OpAlarmGroupOperator(operator)) : []
            this.currentAlarmDefGroups = (_.size(opObj.CurrentAlarmDefGroups) > 0) ? _.map(opObj.CurrentAlarmDefGroups, (defGroup: OpAlarmDefGroup) => new OpAlarmDefGroup(defGroup)) : []
        }
    }

    public toJSON(compressed: boolean = false): any {
        return {
            ID_ALARM_GROUP: this.idAlarmGroup,
            NAME: this.name,
            DESCRIPTION: this.description,
            ID_DISTRIBUTOR: this.idDistributor,
            IS_ENABLED: this.isEnabled,

            DataList: this.dataList == null ? null : this.dataList.ToJSON(),

            _Distributor: this.distributor == null ? null : this.distributor.toJSON(compressed),

            OpState: this.opState,
            CurrentAlarmDefGroups: this.currentAlarmDefGroups != null ? _.map(this.currentAlarmDefGroups, (alarmDefGroup: OpAlarmDefGroup) => alarmDefGroup.toJSON()) : [], 
            CurrentAlarmGroupOperators: this.currentAlarmGroupOperators != null ? _.map(this.currentAlarmGroupOperators, (alarmGroupOperator: OpAlarmGroupOperator) => alarmGroupOperator.toJSON()) : [],

            dynamicValues: this.dynamicValues
        }
    }

    // #endregion

    // #region ICopyable

    public copy(sourceObject: OpAlarmGroup, includeNavigationProperties: boolean, includeCustomProperties: boolean, includeDataList: boolean, includeDynamicValues: boolean): void {

        if (sourceObject == null) {
            return null;
        }

        if (includeNavigationProperties) {
            this.distributor = sourceObject.distributor == null ? null : sourceObject.distributor.clone(includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues)
        }

        if (includeCustomProperties) {
            if (_.size(sourceObject.currentAlarmDefGroups) > 0) {
                if (this.currentAlarmDefGroups == null) {
                    this.currentAlarmDefGroups = []
                }
                this.currentAlarmDefGroups = sourceObject.currentAlarmDefGroups.map(alarmDefGroup => new OpAlarmDefGroup(alarmDefGroup))
            }

            if (_.size(sourceObject.currentAlarmGroupOperators) > 0) {
                if (this.currentAlarmGroupOperators == null) {
                    this.currentAlarmGroupOperators = []
                }
                this.currentAlarmGroupOperators = sourceObject.currentAlarmGroupOperators.map(alarmGroupOperator => new OpAlarmGroupOperator(alarmGroupOperator))
            }
        }

        if (includeDataList) {
            if (sourceObject.dataList != null) {
                _.forEach(sourceObject.dataList.toArray(), (dataList: OpAlarmGroupData) => {
                    this.dataList.push(dataList.clone(includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues))
                })
            }
        }

        if (includeDynamicValues) {
            this.dynamicValues = sourceObject.dynamicValues
        }

        this.idAlarmGroup = sourceObject.idAlarmGroup
        this.name = sourceObject.name
        this.description = sourceObject.description
        this.idDistributor = sourceObject.idDistributor
        this.isEnabled = sourceObject.isEnabled

        this.opState = sourceObject.opState
    }

    public clone(includeNavigationProperties: boolean, includeCustomProperties: boolean, includeDataList: boolean, includeDynamicValues: boolean): OpAlarmGroup {
        const group: OpAlarmGroup = new OpAlarmGroup(null)

        group.copy(this, includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues)

        return group
    }

    // #endregion

    public static loadNavigationProperties(dataProvider: DataProvider, list: OpAlarmGroup[]): JQueryPromise<OpAlarmGroup[]> {
        return Utils.loadDataListNavigationProperties(dataProvider, list)
    }
}
